import { Box, Card } from 'pcln-design-system'
import { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'

import ComponentMetricsBoundary from '@/components/ComponentMetricsBoundary/ComponentMetricsBoundary'
import StyledHeading from '@/components/StyledHeading'
import { GREETING_DESKTOP, vipHeading } from '@/constants/content'
import useBootstrapData from '@/hooks/useBootstrapData'
import useCityNameInFormHeadlineForSEM from '@/hooks/useCityNameInFormHeadlineForSEM'
import useClientSideSignedInData from '@/hooks/useClientSideSignedInData'
import useSEMSearchHeadline from '@/hooks/useSEMSearchHeadline'
import analytics from '@/shared-utils/analytics'
import type { LOCATION_SEARCH_TYPE, ProductType } from '@/types'

import SearchFormProvider from '../../context/SearchFormContext'
import { COMPONENT_ID } from './constants'
import useHeadlineValues from './customHooks/useHeadlineValues'
import { HomepageBumperSlot } from './HomepageBumperSlot'
import Tabs from './Tabs'
import getProductCategory from './utils/getProductCategory'

const StyledCard = styled(Card)`
  border-radius: 24px 24px 6px 6px;
`

export default function SearchFormDesktop({
  currentTab,
  onFormChange,
}: {
  currentTab: ProductType
  onFormChange: (productType: ProductType) => void
}) {
  const [location, setLocation] = useState<LOCATION_SEARCH_TYPE | undefined>()
  const {
    customer: { firstName: firstNameBootstrap },
    signInStatus: { signedIn: serverSideSignedIn },
  } = useBootstrapData()

  const { firstName: clientSideFirstName, signedIn: clientSideSignedIn } = useClientSideSignedInData()

  const firstName = serverSideSignedIn
    ? firstNameBootstrap
    : clientSideSignedIn
      ? clientSideFirstName
      : undefined

  const prePopHeadline = useCityNameInFormHeadlineForSEM(location)
  const SEMHeadline = useSEMSearchHeadline()
  const headlineValues = useHeadlineValues(currentTab)

  const headlineWithName = firstName ? vipHeading.headlineWithName(firstName) : GREETING_DESKTOP
  const defaultHeadline = firstName ? headlineWithName : headlineValues.hotels.headline
  const hotelHeadline = prePopHeadline || SEMHeadline || defaultHeadline
  const updatedHeadlineValues = useMemo(
    () => ({
      ...headlineValues,
      hotels: {
        ...headlineValues.hotels,
        headline: hotelHeadline,
      },
    }),
    [hotelHeadline, headlineValues],
  )

  const [formState, setFormState] = useState(
    getProductCategory(currentTab, updatedHeadlineValues, setLocation, true),
  )
  useEffect(() => {
    if (currentTab === 'hotels' && clientSideFirstName !== undefined) {
      setFormState((prevState) => ({
        ...prevState,
        headline: vipHeading.headlineWithName(clientSideFirstName),
      }))
    }
  }, [clientSideFirstName, currentTab])

  const handleOnTabClick = (productType: ProductType) => {
    if (productType === currentTab) {
      return
    }

    if (productType === 'ai_mode') {
      analytics.fireGA4Event({
        event: 'internal_link',
        attributes: {
          type: 'ai_mode_penny_search',
          link_name: 'ai_mode_search_form_tab',
          page_name: 'homepage',
          product_name: 'penny',
        },
      })
      window.open('/penny', '_blank')
      return
    }

    const selectedProductCategory = getProductCategory(productType, updatedHeadlineValues, setLocation, true)
    setFormState(selectedProductCategory)
    onFormChange(productType)
  }

  return (
    <ComponentMetricsBoundary key={currentTab} componentId={COMPONENT_ID[currentTab]}>
      <Box>
        <StyledHeading
          pl={0}
          pb={[16, null, null, null, 4]}
          pr={[0, null, null, null, '130px']}
          tag='h1'
          color='text.heading'
          text={formState.headline}
          mx={[4, null, null, null, 0]}
          textStyle={['heading2', 'heading3', null, 'heading2', null, 'heading1']}
        />
        <Card
          boxShadowSize='xl'
          borderWidth={0}
          borderRadius='2xl'
          minWidth={[null, null, null, '720px']}
          color='background.lightest'
          mx={[4, null, null, null, 0]}
        >
          <StyledCard borderWidth={0}>
            <Box pt={3}>
              <Tabs initialActiveTab={currentTab} onTabClick={handleOnTabClick} />
            </Box>
          </StyledCard>
          <SearchFormProvider>
            <Box p={3} id={`panel-${currentTab}`}>
              {formState.component}
            </Box>
            <HomepageBumperSlot productType={currentTab}>
              <Box>{formState.bumper}</Box>
            </HomepageBumperSlot>
          </SearchFormProvider>
        </Card>
      </Box>
    </ComponentMetricsBoundary>
  )
}
